import { zodResolver } from "@hookform/resolvers/zod";
import { assertNever, cn, groupBy } from "@theprelude/core";
import {
  Button,
  DateInput,
  DurationInput,
  IconButton,
  Input,
  InputField,
  PlusIcon,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollBar,
  Select,
  SelectFormField,
  SelectOption,
  SelectOptions,
  TrashcanIcon,
} from "@theprelude/ds";
import { Fragment } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { z } from "zod";
import { ColumnFilter } from "@/components/table/columns";
import { Filter, filterSchema } from "@/lib/filter";
import { isValidRegex } from "@/lib/is-valid-regex";
import {
  getOdataId,
  isDurationFilterValue,
  isOdataControlId,
  odataOperatorSchema,
} from "@/lib/odata";
import {
  AdditionalFilter,
  isDurationId,
  prefixDurationId,
} from "./additional-filters";

export type FormFilter = z.infer<typeof formFilterSchema>;
export type FilterFormData = z.infer<typeof filterFormSchema>;

export const formFilterValueSchema = z.object({
  operator: odataOperatorSchema,
  value: z.string().nonempty(),
});

export const formFilterSchema = filterSchema
  .extend({
    value: z.string().nonempty(),
    operator: z.union([odataOperatorSchema, z.literal("")]),
    ors: z.array(formFilterValueSchema).optional(),
  })
  .refine(
    (input) =>
      (input.operator !== "matches" && input.operator !== "does not match") ||
      isValidRegex(input.value),
    { message: "Invalid regular expression", path: ["value"] },
  )
  .refine((input) => !isDurationId(input.id) || isValidDuration(input.value), {
    message: "Duration must be at least 1 day",
    path: ["value"],
  });

const filterFormSchema = z.object({
  filters: z.array(formFilterSchema),
});

const DEFAULT_FILTERS: FormFilter[] = [
  { id: "", operator: "", value: "", ors: [] },
];

type FilterFieldValues = FieldValues & { filters: FormFilter[] };

const isValidDuration = (duration?: string) => {
  if (!duration) {
    return true;
  }
  return Boolean(Number.parseInt(duration, 10));
};

export const getDefaultCast = (
  type: AdditionalFilter["type"] | undefined = "text",
) => {
  const cast = (value: string) => {
    switch (type) {
      case "boolean": {
        return value === "true";
      }
      case "duration":
      case "number": {
        return Number(value);
      }
      case "text":
      case "date": {
        return String(value);
      }
      default: {
        return assertNever(type);
      }
    }
  };

  return ({ operator, value, ors, ...filter }: FormFilter): Filter => {
    if (operator === "") {
      throw new Error('Invalid operator: ""');
    }
    return {
      operator,
      value: cast(value),
      ors: ors?.map((or) => ({ ...or, value: cast(or.value as string) })),
      ...filter,
    };
  };
};

export const DEFAULT_PARSE = (filter: Filter): FormFilter => {
  const formFilter = {
    ...filter,
    value: filter.value.toString(),
    ors: filter.ors?.map((or) => ({
      operator: or.operator,
      value: or.value.toString(),
    })),
  } as FormFilter;

  return formFilter;
};

export function convertFiltersToFormFilters(
  currentFilters: Filter[],
  filters: AdditionalFilter[],
): FormFilter[] {
  return currentFilters.map((filter) => {
    const id = getOdataId({
      ...filter,
      id:
        isDurationFilterValue(filter.value) && !isDurationId(filter.id)
          ? prefixDurationId(filter.id)
          : filter.id,
    });
    const formField = filters.find((c) => c.id === id);
    const parse = formField?.parse ?? DEFAULT_PARSE;
    return parse({ ...filter, id });
  });
}

const determineCategory = (id: string) =>
  isOdataControlId(id) ? "control" : "all";

export function sortFilterFormFields(
  a: AdditionalFilter,
  b: AdditionalFilter,
): number {
  const order = {
    all: 0,
    control: 1,
  };

  const categoryA = determineCategory(a.id);
  const categoryB = determineCategory(b.id);

  if (categoryA !== categoryB) {
    return order[categoryA] - order[categoryB];
  }

  return a.label.localeCompare(b.label);
}

export function convertFormFiltersToFilters(
  currentFilters: FormFilter[],
  filters: AdditionalFilter[],
): Filter[] {
  return currentFilters.map((filter) => {
    const formField = filters.find((c) => c.id === filter.id);
    const cast = formField?.cast ?? getDefaultCast(formField?.type ?? "text");
    return cast({
      ...filter,
      ...formField?.odata,
      isArray: formField?.isArray ?? false,
    });
  });
}

export function FilterForm({
  availableFilters,
  onSubmit,
  onClose,
  onClear,
  defaultFilters,
}: {
  availableFilters: AdditionalFilter[];
  onSubmit: (data: Filter[]) => void;
  onClear: () => void;
  onClose: () => void;
  defaultFilters: Filter[];
}) {
  const form = useForm<FilterFormData>({
    defaultValues: {
      filters:
        defaultFilters.length > 0
          ? convertFiltersToFormFilters(defaultFilters, availableFilters)
          : DEFAULT_FILTERS,
    },
    resolver: zodResolver(filterFormSchema),
  });

  const { control, handleSubmit } = form;

  async function submit(data: FilterFormData) {
    onSubmit(convertFormFiltersToFilters(data.filters, availableFilters));
    onClose();
  }

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters",
  });

  return (
    <FormProvider {...form}>
      <ScrollAreaRoot>
        <ScrollAreaViewport className="max-h-[500px]">
          <form
            className="flex max-w-[80vw] flex-col justify-between gap-2 p-4"
            onSubmit={handleSubmit(submit)}
          >
            <FilterFormFields
              fields={fields}
              onRemove={remove}
              availableFilters={availableFilters}
            />
            <FilterFormActions
              canApply
              onAdd={append}
              onClear={() => {
                onClear();
                onClose();
              }}
            />
          </form>
        </ScrollAreaViewport>
        <ScrollBar orientation="vertical" />
      </ScrollAreaRoot>
    </FormProvider>
  );
}

const findColumnMetaFilter = (
  availableFilters: AdditionalFilter[],
  id: string | undefined,
) => (id ? availableFilters.find((c) => c.id === id) : undefined);

interface FilterFormFieldsProps {
  fields: FormFilter[];
  onRemove: (index: number) => void;
  availableFilters: AdditionalFilter[];
  disabled?: boolean;
  readOnly?: boolean;
}

export const FilterFormFields = ({
  fields,
  ...props
}: FilterFormFieldsProps) => {
  return fields.map((field, index) => (
    <FilterFormField
      key={field.id}
      field={field}
      index={index}
      showConjunction
      {...props}
    />
  ));
};

interface FilterFormFieldProps extends Omit<FilterFormFieldsProps, "fields"> {
  field: FormFilter;
  index: number;
  showConjunction?: boolean;
  onRemove: (index: number) => void;
  availableFilters: AdditionalFilter[];
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

interface FilterValueInputProps {
  type: ColumnFilter["type"] | undefined;
  value: string;
  onChange: (value: string | null) => void;
  name?: string;
  onBlur?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
}

const FilterValueInput = ({
  type,
  value,
  onChange,
  name,
  onBlur,
  readOnly,
  disabled,
}: FilterValueInputProps) => {
  switch (type) {
    case "duration": {
      return (
        <DurationInput
          value={value === "" ? undefined : Number(value)}
          defaultUnit="days"
          onChange={(val) => onChange(val === undefined ? null : String(val))}
          allowedUnits={["days"]}
          readOnly={readOnly}
          disabled={disabled}
          className="flex-1"
        />
      );
    }
    case "date": {
      return (
        <DateInput
          value={value}
          name={name ?? ""}
          onBlur={onBlur}
          wrapperClassName="flex-1"
          onChange={onChange}
          readOnly={readOnly}
          disabled={disabled}
        />
      );
    }
    case "boolean": {
      return (
        <Select
          className="flex-1"
          name={name}
          onBlur={onBlur}
          value={value}
          onChange={(e) => {
            onChange(e.target.value ?? null);
          }}
          readOnly={readOnly}
          disabled={disabled}
        >
          <option disabled value="">
            Select a value
          </option>
          <option value="true">True</option>
          <option value="false">False</option>
        </Select>
      );
    }
    case "number":
    case "text":
    case undefined: {
      return (
        <Input
          name={name}
          onBlur={onBlur}
          value={value}
          type={type === "number" ? "number" : "text"}
          wrapperClassName="flex-1"
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder="Required"
          className="placeholder:text-secondary-foreground"
          required
          readOnly={readOnly}
          disabled={disabled}
        />
      );
    }
    default: {
      return assertNever(type);
    }
  }
};

export const FilterFormField = ({
  field,
  index,
  availableFilters,
  showConjunction,
  onRemove,
  disabled,
  readOnly,
  className,
}: FilterFormFieldProps) => {
  const { control, register, setValue, watch } =
    useFormContext<FilterFieldValues>();

  const currentFilters = watch("filters");
  const {
    fields: orFields,
    append: appendOr,
    remove: removeOr,
  } = useFieldArray({
    control,
    name: `filters.${index}.ors`,
  });

  return (
    <div
      className={cn(
        "grid w-full grid-cols-1 items-start gap-2 md:grid-cols-3",
        className,
      )}
      key={field.id}
    >
      <div className="col-span-1 flex items-end gap-4">
        {showConjunction && index !== 0 && (
          <div className="col-span-1 mb-1.5 w-max rounded-md border border-stroke-line px-2 py-1 text-xs text-secondary-foreground font-medium">
            AND
          </div>
        )}
        <SelectFormField
          {...register(`filters.${index}.id`, {
            onChange: (event) => {
              setValue(`filters.${index}.id`, event.target.value);
              setValue(`filters.${index}.operator`, "");
              setValue(`filters.${index}.value`, "");
              setValue(`filters.${index}.ors`, []);
            },
          })}
          label="Filter Column"
          size="sm"
          required
          readOnly={readOnly}
          disabled={disabled}
          options={getFilterOptions({ availableFilters, currentFilters })}
          className="w-full"
        />
      </div>
      <SelectFormField
        {...register(`filters.${index}.operator`)}
        label="Operator"
        size="sm"
        required
        readOnly={readOnly}
        disabled={disabled}
        options={getOperatorOptions(
          findColumnMetaFilter(availableFilters, currentFilters[index]?.id),
        )}
        className="col-span-1 w-full"
      />
      <Controller
        name={`filters.${index}.value`}
        control={control}
        disabled={disabled}
        render={({ field: valueField, fieldState: { error } }) => {
          const filter = findColumnMetaFilter(
            availableFilters,
            currentFilters[index]?.id,
          );
          const type = filter?.type;

          const onChange = (val: string | null) => {
            setValue(`filters.${index}.value`, val ?? "");
          };

          return (
            <InputField
              label="Value"
              size="sm"
              required
              error={error?.message ?? error?.root?.message}
              className="col-span-1 w-full"
            >
              <div className="flex items-center gap-2">
                <FilterValueInput
                  type={type}
                  value={valueField.value}
                  onChange={onChange}
                  name={valueField.name}
                  onBlur={valueField.onBlur}
                  readOnly={readOnly}
                  disabled={disabled}
                />
                <Button
                  intent="secondary"
                  type="button"
                  size="small"
                  disabled={disabled || !currentFilters[index]?.id}
                  onClick={() =>
                    appendOr({
                      operator: "is",
                      value: "",
                    })
                  }
                >
                  + OR
                </Button>

                <IconButton
                  icon={<TrashcanIcon />}
                  onClick={() => onRemove(index)}
                  disabled={disabled}
                  className="border border-stroke-line p-2"
                />
              </div>
            </InputField>
          );
        }}
      />

      {orFields.length > 0 && (
        <>
          {orFields.map((orField, orIndex) => (
            <Fragment key={orField.id}>
              <div className="col-span-1 flex items-center justify-end pt-7">
                <div className="w-max rounded-md border border-stroke-line px-2 py-1 text-xs text-secondary-foreground font-medium">
                  OR
                </div>
              </div>
              <SelectFormField
                {...register(
                  `filters.${index}.ors.${orIndex}.operator` as const,
                )}
                label="Operator"
                size="sm"
                required
                readOnly={readOnly}
                disabled={disabled}
                options={getOperatorOptions(
                  findColumnMetaFilter(
                    availableFilters,
                    currentFilters[index]?.id,
                  ),
                )}
                className="w-full"
              />
              <Controller
                name={`filters.${index}.ors.${orIndex}.value` as const}
                control={control}
                disabled={disabled}
                render={({ field: orValueField, fieldState: { error } }) => {
                  const filter = findColumnMetaFilter(
                    availableFilters,
                    currentFilters[index]?.id,
                  );
                  const type = filter?.type;

                  const onChange = (val: string | null) => {
                    setValue(
                      `filters.${index}.ors.${orIndex}.value` as const,
                      val ?? "",
                    );
                  };

                  return (
                    <InputField
                      label="Value"
                      size="sm"
                      required
                      error={error?.message ?? error?.root?.message}
                      className="w-full"
                    >
                      <div className="flex items-center gap-2">
                        <FilterValueInput
                          type={type}
                          value={orValueField.value}
                          onChange={onChange}
                          name={orValueField.name}
                          onBlur={orValueField.onBlur}
                          readOnly={readOnly}
                          disabled={disabled}
                        />
                        <IconButton
                          icon={<TrashcanIcon />}
                          onClick={() => removeOr(orIndex)}
                          disabled={disabled}
                          className="border border-stroke-line p-2"
                        />
                      </div>
                    </InputField>
                  );
                }}
              />
            </Fragment>
          ))}
        </>
      )}
    </div>
  );
};

export const getFilterOptions = ({
  availableFilters,
  currentFilters,
}: {
  availableFilters: AdditionalFilter[];
  currentFilters: FormFilter[];
}): SelectOptions<string> => {
  const groupedFilterFields = groupBy(
    availableFilters.sort(sortFilterFormFields),
    (filter) => filter.group ?? "",
  );

  const options = Object.entries(groupedFilterFields).flatMap(
    ([label, filterGroup]): SelectOptions<string> => {
      const items = filterGroup.map((filterField) => ({
        label: filterField.label,
        value: filterField.id,
        disabled: currentFilters.some((filter) => filter.id === filterField.id),
      }));
      return label ? [{ label, children: items }] : items;
    },
  );

  const defaultOption: SelectOption<string> = {
    label: "Select a column",
    value: "",
    disabled: true,
  };

  return [defaultOption, ...options];
};

export const getOperatorOptions = (
  filter: AdditionalFilter | undefined,
): SelectOptions<string> => {
  const options: SelectOption<string>[] =
    filter?.operators?.map((operator) => ({
      label: operator,
      value: operator,
    })) ?? [];

  const defaultOption: SelectOption<string> = {
    label: "Select an operator",
    value: "",
    disabled: true,
  };

  return [defaultOption, ...options];
};

export const FilterFormActions = ({
  canApply = false,
  onAdd,
  onClear,
  disabled = false,
}: {
  canApply?: boolean;
  onAdd: (filter: FormFilter) => void;
  onClear: () => void;
  disabled?: boolean;
}) => (
  <div className="mt-1 flex gap-2">
    <div className="flex gap-2">
      <Button
        icon={<PlusIcon />}
        intent="secondary"
        type="button"
        disabled={disabled}
        onClick={() =>
          onAdd({
            id: "",
            operator: "",
            value: "",
            ors: [],
          })
        }
      >
        Add filter
      </Button>
      {canApply && (
        <Button intent="primary" disabled={disabled}>
          Apply
        </Button>
      )}
    </div>
    <div className="flex gap-2">
      <Button
        intent="text-link"
        type="button"
        disabled={disabled}
        onClick={onClear}
      >
        Clear all
      </Button>
    </div>
  </div>
);

export function getFilterCount(filters: Filter[]) {
  return (
    filters.length +
    filters.reduce((acc, filter) => acc + (filter.ors?.length || 0), 0)
  );
}
